/**
 * Size Guides Handlers
 *
 * All handlers are pure (state, command) => state transformations. Side effects
 * (PV3 dispatches via `sizeGuideCrudPolicy`, subscription wiring, the ML specs
 * proxy fetch) live in policies — never here.
 */

import { Column, Row } from '@appTypes/Spreadsheet';
import { CommandHandlerMap } from '@commandDrivenStateEngine/core/types';
import {
  SizeGuideColumn,
  SizeGuideReadmodelType,
  SizeGuidesState
} from './state';
import { SizeGuidesCommand } from './commands';

type ExtractCommand<T extends string> = Extract<SizeGuidesCommand, { type: T }>;

/**
 * Map a canonical column's `valueType` onto the spreadsheet column `type`. The
 * spreadsheet `Column` has no `role`/`valueType` — the editor tracks the main
 * column separately via `editor.mainColumnKey`.
 */
const spreadsheetColumnType = (
  valueType: SizeGuideColumn['valueType']
): Column['type'] => {
  switch ( valueType ) {
    case 'enum':
      return 'select';
    case 'number_unit':
      return 'numberUnit';
    case 'string':
    default:
      return 'string';
  }
};

/** Project a canonical guide's columns onto spreadsheet `Column[]`. */
const toEditorColumns = ( guide: SizeGuideReadmodelType ): Column[] =>
  guide.columns.map(( column ) => {
    // A `number_unit` column is only a unit cell when it actually carries a
    // unit. Without one, the shared `CellNumberUnit` saves `${value} ${unit}`
    // with `unit === undefined` — producing "11 undefined" on edit / new row.
    // Fall back to a plain text cell so the value round-trips cleanly.
    const hasUnit = column.unit !== undefined && column.unit !== '';
    const isUnitCell = column.valueType === 'number_unit' && hasUnit;
    return {
      id: column.key,
      name: column.label,
      type: isUnitCell
        ? 'numberUnit'
        : spreadsheetColumnType(
          column.valueType === 'number_unit' ? 'string' : column.valueType
        ),
      ...( isUnitCell
        ? { units: { allowedUnits: [column.unit as string], defaultUnit: column.unit as string }}
        : {})
    };
  });

/** Project a canonical guide's rows onto spreadsheet `Row[]`. */
const toEditorRows = ( guide: SizeGuideReadmodelType ): Row[] =>
  guide.rows.map(( row ) => ({
    id: row.id,
    values: { ...row.cells }
  }));

// =============================================================================
// DATA HANDLERS — replace the respective data slices (api source).
// =============================================================================

// A paginated page result: `guides` becomes the current page; `totalCount`
// records the full server-side count for the pager.
const handleSetPageResult = (
  state: SizeGuidesState,
  cmd: ExtractCommand<'sizeGuides/data/setPageResult'>
): SizeGuidesState => ({
  ...state,
  data: {
    ...state.data,
    guides: cmd.payload.guides,
    list: { ...state.data.list, totalCount: cmd.payload.count, loading: false }
  }
});

const handleSetLoading = (
  state: SizeGuidesState,
  cmd: ExtractCommand<'sizeGuides/data/setLoading'>
): SizeGuidesState => ({
  ...state,
  data: { ...state.data, list: { ...state.data.list, loading: cmd.payload.loading }}
});

// =============================================================================
// LIST HANDLERS — gallery search + pagination view-state (the data policy
// reacts to these and refetches the matching page server-side).
// =============================================================================

// Search change resets to page 1 (a new query's result set is page-relative).
const handleListSetSearch = (
  state: SizeGuidesState,
  cmd: ExtractCommand<'sizeGuides/list/setSearch'>
): SizeGuidesState => ({
  ...state,
  data: {
    ...state.data,
    list: { ...state.data.list, searchQuery: cmd.payload.searchQuery, page: 1 }
  }
});

const handleListSetPage = (
  state: SizeGuidesState,
  cmd: ExtractCommand<'sizeGuides/list/setPage'>
): SizeGuidesState => ({
  ...state,
  data: { ...state.data, list: { ...state.data.list, page: cmd.payload.page }}
});

// =============================================================================
// CRUD HANDLERS — push/pop the privileged op queue drained by the CRUD policy.
// =============================================================================

const handleCrudUpsert = (
  state: SizeGuidesState,
  cmd: ExtractCommand<'sizeGuides/crud/upsert'>
): SizeGuidesState => ({
  ...state,
  loading: {
    ...state.loading,
    pendingCrud: [
      ...state.loading.pendingCrud,
      {
        kind: 'upsert',
        id: cmd.payload.id,
        guide: cmd.payload.guide,
        isImport: cmd.payload.isImport
      }
    ]
  }
});

const handleCrudDelete = (
  state: SizeGuidesState,
  cmd: ExtractCommand<'sizeGuides/crud/delete'>
): SizeGuidesState => ({
  ...state,
  loading: {
    ...state.loading,
    pendingCrud: [
      ...state.loading.pendingCrud,
      { kind: 'delete', id: cmd.payload.id }
    ]
  }
});

const handleCrudDone = (
  state: SizeGuidesState,
  cmd: ExtractCommand<'sizeGuides/crud/done'>
): SizeGuidesState => {
  // Pop the first matching pending op (FIFO drain), matched by id.
  const idx = state.loading.pendingCrud.findIndex(
    ( op ) => op.id === cmd.payload.id
  );
  if ( idx === -1 ) return state;
  const next = state.loading.pendingCrud.slice();
  next.splice( idx, 1 );
  return {
    ...state,
    loading: { ...state.loading, pendingCrud: next }
  };
};

// =============================================================================
// EDITOR HANDLERS — the SpreadsheetEditor working copy for one selected guide.
// =============================================================================

const handleEditorSelect = (
  state: SizeGuidesState,
  cmd: ExtractCommand<'sizeGuides/editor/select'>
): SizeGuidesState => {
  const { guideId } = cmd.payload;

  // Deselect: clear the working copy back to an empty editor.
  if ( guideId === null ) {
    return {
      ...state,
      editor: {
        ...state.editor,
        selectedGuideId: null,
        columns: [],
        rows: [],
        mainColumnKey: null,
        dirty: false
      }
    };
  }

  const guide = state.data.guides.find(( g ) => g.id === guideId );

  // Unknown guide: record the selection but leave the working copy empty (the
  // data subscription may not have delivered the guide yet).
  if ( guide === undefined ) {
    return {
      ...state,
      editor: {
        ...state.editor,
        selectedGuideId: guideId,
        columns: [],
        rows: [],
        mainColumnKey: null,
        dirty: false
      }
    };
  }

  const mainColumn = guide.columns.find(( column ) => column.role === 'main' );

  return {
    ...state,
    editor: {
      ...state.editor,
      selectedGuideId: guideId,
      columns: toEditorColumns( guide ),
      rows: toEditorRows( guide ),
      mainColumnKey: mainColumn ? mainColumn.key : null,
      dirty: false
    }
  };
};

export const handlers: CommandHandlerMap<SizeGuidesState, SizeGuidesCommand> = {
  'sizeGuides/data/setPageResult': handleSetPageResult,
  'sizeGuides/data/setLoading': handleSetLoading,
  'sizeGuides/list/setSearch': handleListSetSearch,
  'sizeGuides/list/setPage': handleListSetPage,
  'sizeGuides/crud/upsert': handleCrudUpsert,
  'sizeGuides/crud/delete': handleCrudDelete,
  'sizeGuides/crud/done': handleCrudDone,
  'sizeGuides/editor/select': handleEditorSelect
};
